import { useSyncExternalStore } from "react";
import type { components } from "@erp/api-client";

export type SessionBootstrap = components["schemas"]["SessionBootstrap"];

/** Prefix only; the actual key is namespaced by company and user. */
export const BRANCH_STORAGE_KEY = "erp.branch";

export interface BranchState {
  currentBranchId: string | null;
  userId: string | null;
  companyId: string | null;
  allowedBranchIds: readonly string[];
  defaultBranchId: string | null;
}

type Listener = () => void;

const EMPTY_STATE: BranchState = {
  currentBranchId: null,
  userId: null,
  companyId: null,
  allowedBranchIds: [],
  defaultBranchId: null,
};

let state: BranchState = EMPTY_STATE;
const listeners = new Set<Listener>();

export function branchStorageKey(userId: string, companyId: string): string {
  return `${BRANCH_STORAGE_KEY}:${companyId}:${userId}`;
}

function currentStorageKey(): string | null {
  return state.userId !== null && state.companyId !== null
    ? branchStorageKey(state.userId, state.companyId)
    : null;
}

function readStored(key: string): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStored(key: string, branchId: string | null): void {
  if (typeof window === "undefined") return;
  try {
    if (branchId === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, branchId);
  } catch {
    // Storage can be unavailable in private mode. In-memory selection remains valid.
  }
}

function publish(next: BranchState): void {
  if (
    next.currentBranchId === state.currentBranchId &&
    next.userId === state.userId &&
    next.companyId === state.companyId &&
    next.allowedBranchIds === state.allowedBranchIds &&
    next.defaultBranchId === state.defaultBranchId
  ) {
    return;
  }
  state = next;
  for (const listener of listeners) listener();
}

function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function useBranchStoreHook(): BranchState;
function useBranchStoreHook<T>(selector: (value: BranchState) => T): T;
function useBranchStoreHook<T>(selector?: (value: BranchState) => T): BranchState | T {
  return useSyncExternalStore(
    subscribe,
    () => (selector ? selector(state) : state),
    () => (selector ? selector(state) : state),
  );
}

export const useBranchStore = Object.assign(useBranchStoreHook, {
  getState: () => state,
  subscribe,
});

function isAllowed(branchId: string | null, allowedBranchIds: readonly string[]): boolean {
  return branchId === null || allowedBranchIds.includes(branchId);
}

function fallbackOf(session: SessionBootstrap): string | null {
  if (
    session.acting_branch_id !== null &&
    session.allowed_branch_ids.includes(session.acting_branch_id)
  ) {
    return session.acting_branch_id;
  }
  if (
    session.default_branch_id !== null &&
    session.allowed_branch_ids.includes(session.default_branch_id)
  ) {
    return session.default_branch_id;
  }
  return null;
}

/**
 * Bind branch state to one authenticated user/company. A persisted value wins
 * only while it remains in the server-authoritative active assignment list;
 * stale or unauthorized values are removed before falling back to the server
 * resolved acting/default branch.
 */
export function configureBranchContext(session: SessionBootstrap): void {
  const key = branchStorageKey(session.id, session.company_id);
  const stored = readStored(key);
  const storedAllowed = stored !== null && session.allowed_branch_ids.includes(stored);
  if (stored !== null && !storedAllowed) writeStored(key, null);

  const currentBranchId = storedAllowed ? stored : fallbackOf(session);
  writeStored(key, currentBranchId);
  publish({
    currentBranchId,
    userId: session.id,
    companyId: session.company_id,
    allowedBranchIds: [...session.allowed_branch_ids],
    defaultBranchId: session.default_branch_id,
  });
}

/** Clear in-memory authority on sign-out without deleting per-user preferences. */
export function clearBranchContext(): void {
  publish(EMPTY_STATE);
}

export function useCurrentBranch(): {
  branchId: string | null;
  setBranchId: (branchId: string | null) => void;
} {
  const branchId = useBranchStore((value) => value.currentBranchId);
  return { branchId, setBranchId: setCurrentBranchId };
}

export function getCurrentBranchId(): string | null {
  return state.currentBranchId;
}

/**
 * Selection is constrained by the current bootstrap. This prevents a stale
 * component or manually edited storage value from minting branch authority.
 */
export function setCurrentBranchId(branchId: string | null): void {
  // Unbound state is useful to isolated screen tests; production binds before
  // rendering the authenticated shell. Once bound, the server list is final.
  const next =
    state.userId === null || isAllowed(branchId, state.allowedBranchIds) ? branchId : null;
  const key = currentStorageKey();
  if (key !== null) writeStored(key, next);
  publish({ ...state, currentBranchId: next });
}

/** Re-read the current identity's preference and re-apply assignment checks. */
export function rehydrateBranchStore(): void {
  const key = currentStorageKey();
  if (key === null) {
    publish({ ...state, currentBranchId: null });
    return;
  }
  const stored = readStored(key);
  if (stored !== null && state.allowedBranchIds.includes(stored)) {
    publish({ ...state, currentBranchId: stored });
    return;
  }
  if (stored !== null) writeStored(key, null);
  const fallback = isAllowed(state.defaultBranchId, state.allowedBranchIds)
    ? state.defaultBranchId
    : null;
  writeStored(key, fallback);
  publish({ ...state, currentBranchId: fallback });
}
